import { Close, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import theme from "../../theme/theme";
import { useState, useEffect } from "react";

export const EditLeadDialog = ({
  openEditDialog,
  setOpenEditDialog,
  fetchingLead,
  selectedLead,
  setSelectedLead,
  updateLead,
  errors,
  updatingLead,
}) => {
  const liftTypes = [
    { value: "passenger", label: "Passenger" },
    { value: "freight", label: "Freight" },
    { value: "home", label: "Home" },
    { value: "hospital", label: "Hospital" },
    { value: "goods", label: "Goods" },
    { value: "stretcher", label: "Stretcher" },
  ];

  const doorTypes = [
    { value: "manual", label: "Manual" },
    { value: "automatic", label: "Automatic" },
    { value: "semi-automatic", label: "Semi-Automatic" },
  ];

  const configurations = [
    { value: "mr", label: "MR" },
    { value: "semi-mrl", label: "Semi-MRL" },
    { value: "mrl", label: "MRL" },
    { value: "hydraulic", label: "Hydraulic" },
    { value: "belt", label: "Belt" },
  ];

  const visionTypes = [
    { value: "big_vision", label: "Big Vision" },
    { value: "small_vision", label: "Small Vision" },
    { value: "half_vision", label: "Half Vision" },
    { value: "no_vision", label: "No Vision" },
  ];

  const manualDoorTypes = [
    { value: "ms_collapsible_gate", label: "M.S Collapsible Gate" },
    { value: "ms_ifg_gate", label: "M.S IFG Gate" },
    { value: "ms_swing_door", label: "M.S Swing Door" },
    { value: "ss_swing_door", label: "S.S Swing Door" },
  ];

  const doorFrames = [
    { value: "ms", label: "M.S" },
    { value: "ss", label: "S.S" },
  ];

  return (
    <Dialog
      open={openEditDialog}
      onClose={() => setOpenEditDialog(false)}
      PaperProps={{
        sx: {
          borderRadius: theme.dialog.borderRadius,
          minWidth: theme.dialog.minWidth,
          maxWidth: "600px",
          width: "100%",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={"bold"}
            color={theme.palette.primary.main}
          >
            Edit Lead
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={() => setOpenEditDialog(false)}>
            <Close />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ p: 4, maxHeight: "70vh", overflowY: "auto" }}>
        {fetchingLead ? (
          <>
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
          </>
        ) : (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Client Details
            </Typography>
            <TextField
              label="Client Name"
              fullWidth
              value={selectedLead.client_name || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  client_name: e.target.value,
                })
              }
              error={!!errors.client_name}
              helperText={errors.client_name}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Client Email"
              fullWidth
              value={selectedLead.client_email || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  client_email: e.target.value,
                })
              }
              error={!!errors.client_email}
              helperText={errors.client_email}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Client Phone"
              fullWidth
              value={selectedLead.client_phone || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  client_phone: e.target.value,
                })
              }
              error={!!errors.client_phone}
              helperText={errors.client_phone}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Client Address"
              fullWidth
              multiline
              rows={3}
              value={selectedLead.client_address || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  client_address: e.target.value,
                })
              }
              error={!!errors.client_address}
              helperText={errors.client_address}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Lift Specifications
            </Typography>
            <TextField
              label="Lift Type"
              fullWidth
              select
              value={selectedLead.lift_type || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  lift_type: e.target.value,
                  // Goods lifts are quoted by load (kg), not persons.
                  capacity_persons:
                    e.target.value === "goods"
                      ? ""
                      : selectedLead.capacity_persons,
                })
              }
              error={!!errors.lift_type}
              helperText={errors.lift_type}
              sx={{ mb: 2 }}
            >
              {liftTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Cabin Type"
              fullWidth
              value={selectedLead.cabin_type || ""}
              onChange={(e) =>
                setSelectedLead({ ...selectedLead, cabin_type: e.target.value })
              }
              error={!!errors.cabin_type}
              helperText={errors.cabin_type}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Cabin Construction"
              fullWidth
              value={selectedLead.cabin_construction || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  cabin_construction: e.target.value,
                })
              }
              error={!!errors.cabin_construction}
              helperText={errors.cabin_construction}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              {selectedLead.lift_type !== "goods" && (
                <TextField
                  label="Capacity (Persons)"
                  fullWidth
                  type="number"
                  value={selectedLead.capacity_persons || ""}
                  onChange={(e) =>
                    setSelectedLead({
                      ...selectedLead,
                      capacity_persons: e.target.value,
                    })
                  }
                  error={!!errors.capacity_persons}
                  helperText={errors.capacity_persons}
                />
              )}
              <TextField
                label="Capacity (Kg)"
                fullWidth
                type="number"
                value={selectedLead.capacity_kg || ""}
                onChange={(e) =>
                  setSelectedLead({
                    ...selectedLead,
                    capacity_kg: e.target.value,
                  })
                }
                error={!!errors.capacity_kg}
                helperText={errors.capacity_kg}
              />
            </Box>
            <TextField
              label="No of Floors"
              fullWidth
              type="number"
              value={selectedLead.no_of_floors || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  no_of_floors: e.target.value,
                })
              }
              error={!!errors.no_of_floors}
              helperText={errors.no_of_floors}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Shaft Length"
                fullWidth
                value={selectedLead.shaft_length || ""}
                onChange={(e) =>
                  setSelectedLead({
                    ...selectedLead,
                    shaft_length: e.target.value,
                  })
                }
                error={!!errors.shaft_length}
                helperText={errors.shaft_length}
              />
              <TextField
                label="Shaft Width"
                fullWidth
                value={selectedLead.shaft_width || ""}
                onChange={(e) =>
                  setSelectedLead({
                    ...selectedLead,
                    shaft_width: e.target.value,
                  })
                }
                error={!!errors.shaft_width}
                helperText={errors.shaft_width}
              />
            </Box>
            <TextField
              label="Door Type"
              fullWidth
              select
              value={selectedLead.door_type || ""}
              onChange={(e) => {
                const isManual = e.target.value === "manual";
                const isAutomatic = e.target.value === "automatic";
                setSelectedLead({
                  ...selectedLead,
                  door_type: e.target.value,
                  // Swing + manual door sub-type only for manual; vision only for automatic.
                  is_swing_door: isManual ? selectedLead.is_swing_door : false,
                  manual_door_type: isManual
                    ? selectedLead.manual_door_type
                    : "",
                  vision_type: isAutomatic ? selectedLead.vision_type : "",
                });
              }}
              error={!!errors.door_type}
              helperText={errors.door_type}
              sx={{ mb: 2 }}
            >
              {doorTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {selectedLead.door_type === "manual" && (
              <>
                <TextField
                  label="Manual Door Type"
                  fullWidth
                  select
                  value={selectedLead.manual_door_type || ""}
                  onChange={(e) =>
                    setSelectedLead({
                      ...selectedLead,
                      manual_door_type: e.target.value,
                    })
                  }
                  error={!!errors.manual_door_type}
                  helperText={errors.manual_door_type}
                  sx={{ mb: 2 }}
                >
                  {manualDoorTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!selectedLead.is_swing_door}
                      onChange={(e) =>
                        setSelectedLead({
                          ...selectedLead,
                          is_swing_door: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Swing"
                  sx={{ mb: 2, display: "block" }}
                />
              </>
            )}
            {selectedLead.door_type === "automatic" && (
              <TextField
                label="Vision Type"
                fullWidth
                select
                value={selectedLead.vision_type || ""}
                onChange={(e) =>
                  setSelectedLead({
                    ...selectedLead,
                    vision_type: e.target.value,
                  })
                }
                error={!!errors.vision_type}
                helperText={errors.vision_type}
                sx={{ mb: 2 }}
              >
                {visionTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Door Frame"
              fullWidth
              select
              value={selectedLead.door_frame || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  door_frame: e.target.value,
                })
              }
              error={!!errors.door_frame}
              helperText={errors.door_frame}
              sx={{ mb: 2 }}
            >
              {doorFrames.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Lift Configuration"
              fullWidth
              select
              value={selectedLead.lift_configuration || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  lift_configuration: e.target.value,
                })
              }
              error={!!errors.lift_configuration}
              helperText={errors.lift_configuration}
              sx={{ mb: 2 }}
            >
              {configurations.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Additional Notes"
              fullWidth
              multiline
              rows={3}
              value={selectedLead.additional_notes || ""}
              onChange={(e) =>
                setSelectedLead({
                  ...selectedLead,
                  additional_notes: e.target.value,
                })
              }
              error={!!errors.additional_notes}
              helperText={errors.additional_notes}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Status"
              fullWidth
              select
              value={selectedLead.status || ""}
              onChange={(e) =>
                setSelectedLead({ ...selectedLead, status: e.target.value })
              }
              error={!!errors.status}
              helperText={errors.status}
              sx={{ mb: 2 }}
            >
              {["pending", "approved", "rejected"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={() => setOpenEditDialog(false)}
          sx={{
            backgroundColor: theme.button.backgroundColor,
            color: "#000",
            borderRadius: theme.button.borderRadius,
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => updateLead()}
          sx={{
            backgroundColor: theme.button.editBackgroundColor,
            borderRadius: theme.button.borderRadius,
          }}
          disabled={updatingLead}
          startIcon={<Save />}
        >
          {updatingLead ? "Updating..." : "Update Lead"}
        </Button>
      </Box>
    </Dialog>
  );
};
